import { MATERIALS, COLORS, calculateModuleCost, formatPrice, FILLING_PRESETS } from '@/lib/furnitureData';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Copy, Trash2, RotateCw, Box } from 'lucide-react';

export default function CustomizationPanel({
  selectedModule, modules,
  selectedModuleId, onSelectModule,
  onUpdateModule, onDeleteModule, onCopyModule, onRotateModule, onApplyFilling
}) {
  const totalPrice = modules.reduce((sum, mod) => sum + calculateModuleCost(mod), 0);

  const mod = selectedModule;
  const moduleCost = mod ? calculateModuleCost(mod) : 0;
  const isBody = mod?.type === 'body' || mod?.type === 'frame';
  const isRod = mod?.type === 'hanging_rod';
  const maxDepth = Math.max(...modules.map(m => m.depth || 0), 100);
  const maxZPos = mod ? Math.max(0, Math.round(maxDepth - (mod.depth || 0))) : 0;
  const canEditZ = mod ? !['body', 'frame', 'headboard', 'tabletop', 'countertop', 'top_panel'].includes(mod.type) : false;

  const handleChange = (key, value) => {
    const updates = { [key]: value };
    if (key === 'color_id') {
      const color = COLORS.find(c => c.id === value);
      if (color) updates.color_hex = color.hex;
    }
    if (key === 'material' && value && !mod.color_id) {
      const defaultColor = COLORS[0];
      if (defaultColor) {
        updates.color_id = defaultColor.id;
        updates.color_hex = defaultColor.hex;
      }
    }
    onUpdateModule(mod.id, updates);
  };

  return (
    <div className="w-72 bg-[#FAFAFA] border-l border-[#E0DDD8] flex flex-col shrink-0" data-testid="customization-panel">
      <div className="p-4 border-b border-[#E0DDD8]">
        <h3 className="text-xs font-semibold text-[#5C5C5C] uppercase tracking-wider">
          На сцене
          <span className="ml-1.5 text-[10px] font-normal text-[#5C5C5C]/60">({modules.length})</span>
        </h3>
      </div>

      <ScrollArea className="max-h-40 border-b border-[#E0DDD8] custom-scrollbar">
        <div className="p-2 space-y-0.5">
          {modules.map((sceneModule) => {
            const isSelected = selectedModuleId === sceneModule.id;
            return (
              <div
                key={sceneModule.id}
                className={`flex items-center gap-2 px-2 py-1.5 rounded-sm cursor-pointer transition-colors ${
                  isSelected ? 'bg-[#C25934]/10 border border-[#C25934]/30'
                    : 'border border-transparent hover:bg-black/[0.03] hover:border-[#E0DDD8]'
                }`}
                onClick={() => onSelectModule(sceneModule.id)}
                data-testid={`scene-module-${sceneModule.id}`}
              >
                <div className="w-3 h-3 rounded-full shrink-0 border"
                  style={{ backgroundColor: sceneModule.color_hex || '#C4A76C',
                    borderColor: isSelected ? '#C25934' : 'rgba(0,0,0,0.1)' }} />
                <Box className={`w-3 h-3 shrink-0 ${isSelected ? 'text-[#C25934]' : 'text-[#5C5C5C]'}`} />
                <span className={`text-[11px] truncate flex-1 ${isSelected ? 'font-semibold text-[#C25934]' : 'text-[#1C1C1C]'}`}>
                  {sceneModule.name_ru}
                </span>
                <span className="mono-text text-[9px] text-[#5C5C5C]/60 shrink-0">
                  {sceneModule.width}x{sceneModule.height}
                </span>
              </div>
            );
          })}
        </div>
      </ScrollArea>

      <div className="p-4 border-b border-[#E0DDD8]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <div className="min-w-0">
              {mod ? (
                <>
                  <h3 className="text-sm font-semibold text-[#1C1C1C] truncate" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    {mod.name_ru}
                  </h3>
                  <p className="mono-text text-[10px] text-[#5C5C5C] mt-0.5">
                    {mod.type} / {formatPrice(moduleCost)}
                  </p>
                </>
              ) : (
                <h3 className="text-xs font-semibold text-[#5C5C5C] uppercase tracking-wider">Настройки</h3>
              )}
            </div>
          </div>
          {mod && (
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#5C5C5C] hover:text-[#C25934]"
                onClick={() => onRotateModule && onRotateModule(mod.id)} data-testid="panel-rotate-btn" title="Повернуть 90° (R)">
                <RotateCw className="w-3.5 h-3.5" />
              </Button>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#5C5C5C] hover:text-[#C25934]"
                onClick={() => onCopyModule(mod.id)} data-testid="panel-copy-btn" title="Копировать">
                <Copy className="w-3.5 h-3.5" />
              </Button>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                onClick={() => onDeleteModule(mod.id)} data-testid="panel-delete-btn" title="Удалить">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {mod ? (
        <ScrollArea className="flex-1 custom-scrollbar">
          <div className="p-4 space-y-5">
          {/* Filling presets for body modules */}
          {isBody && (
            <>
              <div>
                <label className="text-xs font-semibold text-[#5C5C5C] uppercase tracking-wider mb-2 block">
                  Варианты наполнения
                </label>
                <div className="grid grid-cols-3 gap-1.5">
                  {FILLING_PRESETS.map(preset => (
                    <button
                      key={preset.id}
                      onClick={() => onApplyFilling(preset.id, mod)}
                      className="flex flex-col items-center p-2 rounded-sm border border-[#E0DDD8] hover:border-[#C25934] hover:bg-[#C25934]/5 transition-colors group"
                      data-testid={`filling-${preset.id}`}
                      title={preset.desc}
                    >
                      <FillingPreview preset={preset} />
                      <span className="text-[9px] text-[#5C5C5C] group-hover:text-[#C25934] mt-1 text-center leading-tight">
                        {preset.nameRu}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Dimensions */}
          <div>
            <label className="text-xs font-semibold text-[#5C5C5C] uppercase tracking-wider mb-3 block">Размеры</label>
            <div className="space-y-3">
              <DimensionSlider label="Ширина" value={mod.width} unit="см"
                min={5} max={300} onChange={(v) => handleChange('width', v)} testId="dim-width" />
              <DimensionSlider label="Высота" value={mod.height} unit="см"
                min={1} max={300} onChange={(v) => handleChange('height', v)} testId="dim-height" />
              <DimensionSlider label="Глубина" value={mod.depth} unit="см"
                min={1} max={100} onChange={(v) => handleChange('depth', v)} testId="dim-depth" />
            </div>
          </div>

          <Separator className="bg-[#E0DDD8]" />

          {/* Position */}
          <div>
            <label className="text-xs font-semibold text-[#5C5C5C] uppercase tracking-wider mb-3 block">Позиция</label>
            <div className="space-y-3">
              <DimensionSlider
                label="X"
                value={Math.round(mod.x || 0)}
                unit="см"
                min={0}
                max={400}
                onChange={(v) => handleChange('x', v)}
                testId="pos-x"
              />
              <DimensionSlider
                label="Y"
                value={Math.round(mod.y || 0)}
                unit="см"
                min={0}
                max={400}
                onChange={(v) => handleChange('y', v)}
                testId="pos-y"
              />
              {canEditZ && (
                <DimensionSlider
                  label="Z"
                  value={Math.round(mod.z_pos || 0)}
                  unit="см"
                  min={0}
                  max={maxZPos}
                  onChange={(v) => handleChange('z_pos', v)}
                  testId="pos-z"
                />
              )}
            </div>
          </div>

          <Separator className="bg-[#E0DDD8]" />

          {/* Material */}
          <div>
            <label className="text-xs font-semibold text-[#5C5C5C] uppercase tracking-wider mb-2 block">Материал</label>
            <Select value={mod.material || undefined} onValueChange={(v) => handleChange('material', v)}>
              <SelectTrigger
                className={`h-9 text-sm rounded-sm border-[#E0DDD8] ${!mod.material ? 'border-[#C25934] text-[#C25934]' : ''}`}
                data-testid="material-select"
              >
                <SelectValue placeholder="Не выбрано — выберите материал" />
              </SelectTrigger>
              <SelectContent>
                {MATERIALS.map(m => (
                  <SelectItem key={m.id} value={m.id} data-testid={`material-${m.id}`}>
                    <span className="flex items-center justify-between w-full gap-2">
                      {m.nameRu}
                      <span className="mono-text text-[10px] text-[#5C5C5C]">x{m.multiplier}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isRod && (
            <>
              <Separator className="bg-[#E0DDD8]" />
              <div>
                <label className="text-xs font-semibold text-[#5C5C5C] uppercase tracking-wider mb-3 block">Параметры штанги</label>
                <div className="space-y-3">
                  <DimensionSlider
                    label="Толщина"
                    value={Math.round(((mod.height || 0) + (mod.depth || 0)) / 2)}
                    unit="см"
                    min={1}
                    max={10}
                    onChange={(v) => {
                      handleChange('height', v);
                      handleChange('depth', v);
                    }}
                    testId="rod-thickness"
                  />
                  <div>
                    <label className="text-xs text-[#5C5C5C] mb-1 block">Профиль</label>
                    <Select value={mod.rod_profile || 'round'} onValueChange={(v) => handleChange('rod_profile', v)}>
                      <SelectTrigger className="h-8 text-xs rounded-sm border-[#E0DDD8]" data-testid="rod-profile-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="round">Круглый</SelectItem>
                        <SelectItem value="oval">Овальный</SelectItem>
                        <SelectItem value="square">Квадратный</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </>
          )}

          <Separator className="bg-[#E0DDD8]" />

          {/* Color */}
          <div>
            <label className="text-xs font-semibold text-[#5C5C5C] uppercase tracking-wider mb-2 block">Цвет</label>
            <div className="flex flex-wrap gap-2">
              {COLORS.map(c => (
                <button
                  key={c.id}
                  className={`color-swatch ${mod.color_id === c.id ? 'active' : ''}`}
                  style={{ backgroundColor: c.hex }}
                  onClick={() => handleChange('color_id', c.id)}
                  title={c.nameRu}
                  data-testid={`color-${c.id}`}
                />
              ))}
            </div>
            <p className="mono-text text-[10px] text-[#5C5C5C] mt-2">
              {COLORS.find(c => c.id === mod.color_id)?.nameRu || '—'}
            </p>
          </div>

          </div>
        </ScrollArea>
      ) : (
        <div className="flex-1 flex items-center justify-center p-6">
          <p className="text-xs text-[#5C5C5C] text-center">Выберите модуль на сцене или на холсте для редактирования</p>
        </div>
      )}

      <CostSummary totalPrice={totalPrice} modules={modules} />
    </div>
  );
}

function FillingPreview({ preset }) {
  if (preset.grid) {
    const { cols, rows } = preset.grid;
    return (
      <div
        className="w-10 h-16 border border-[#d4d0cb] rounded-[1px] grid bg-white overflow-hidden"
        style={{
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gridTemplateRows: `repeat(${rows}, 1fr)`,
          gap: '1px',
          padding: '1px',
          background: '#5C5C5C',
        }}
      >
        {Array.from({ length: cols * rows }).map((_, i) => (
          <div key={i} className="bg-white" />
        ))}
      </div>
    );
  }
  return (
    <div className="w-10 h-16 border border-[#d4d0cb] rounded-[1px] relative bg-white overflow-hidden">
      {(preset.modules || []).map((m, i) => {
        if (m.type === 'shelf' || m.type === 'board') {
          return <div key={i} className="absolute left-[2px] right-[2px] h-[1px] bg-[#5C5C5C]"
            style={{ top: `${m.relY * 100}%` }} />;
        }
        if (m.type === 'hanging_rod') {
          return (
            <div key={i} className="absolute left-1/2 -translate-x-1/2" style={{ top: `${m.relY * 100}%` }}>
              <div className="w-0 h-0 border-l-[3px] border-r-[3px] border-b-[4px] border-transparent border-b-[#5C5C5C]" />
            </div>
          );
        }
        if (m.type === 'drawer') {
          const h = (m.relH || 0.12) * 100;
          return <div key={i} className="absolute left-[2px] right-[2px] border border-[#5C5C5C] rounded-[1px]"
            style={{ top: `${m.relY * 100}%`, height: `${h}%` }}>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[4px] h-[1px] bg-[#5C5C5C]" />
          </div>;
        }
        return null;
      })}
    </div>
  );
}

function DimensionSlider({ label, value, unit, min, max, onChange, testId }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs text-[#5C5C5C]">{label}</span>
        <div className="flex items-center gap-1">
          <Input
            type="number"
            value={value}
            onChange={(e) => onChange(Math.max(min, Math.min(max, Number(e.target.value) || min)))}
            className="mono-text w-16 h-6 text-xs text-right border-[#E0DDD8] rounded-sm px-1"
            data-testid={testId}
          />
          <span className="mono-text text-[10px] text-[#5C5C5C]">{unit}</span>
        </div>
      </div>
      <Slider
        value={[value]}
        onValueChange={([v]) => onChange(v)}
        min={min} max={max} step={1}
        className="w-full"
      />
    </div>
  );
}

function CostSummary({ totalPrice, modules }) {
  return (
    <div className="p-4 border-t border-[#E0DDD8] bg-white" data-testid="cost-summary">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-[#5C5C5C]">Модулей:</span>
        <span className="mono-text text-xs text-[#1C1C1C]">{modules.length}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-[#1C1C1C]">Итого:</span>
        <span className="mono-text text-lg font-bold text-[#C25934]" data-testid="total-price">
          {formatPrice(totalPrice)}
        </span>
      </div>
    </div>
  );
}
